import { useNavigate } from "react-router";
import Hdashboard from "../../../components/Hdashboard";
import React, { useEffect, useState } from "react";
import { get_contactus_list } from "../../../api/apiHandler";
import DataTable from "react-data-table-component";
import FontAwesome from "react-fontawesome";
import JqueryLoad from "../../../JqueryLoad/JqueryLoad";
import { CButton, CFormInput, CInputGroup } from "@coreui/react";
import { Link } from "react-router-dom";
import customStyles from "../../../utils/dataTableStyles";
import useDebounce from "../../../utils/useDebounce";

export default function ContactUs({ isLoader }) {

    const [data, setData] = useState([])
    const navigate = useNavigate()
    const [search, setSearch] = useState('');
    const debounceSearch = useDebounce(search, 750);

    /* ===== FILTERS - START ===== */
    // Same filter behaviour as the New Classes listing : the selected values are sent
    // to the listing API and an empty value lists all the records.
    const [replyStatus, setReplyStatus] = useState("");       // Status of Message : "" | new | replied
    const [currentStatus, setCurrentStatus] = useState("");   // Current Status : "" | one of currentStatusOptions
    // toggled on every filter change so the table goes back to the first page
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

    const currentStatusOptions = [
        "New to StudentScholars",
        "Currently Registered on StudentScholars",
        "Currently Teaching in StudentScholars",
    ];

    const handleReplyStatusFilter = (type) => {
        setReplyStatus(type);
        setResetPaginationToggle((prev) => !prev);
    };

    const handleCurrentStatusFilter = (type) => {
        setCurrentStatus(type);
        setResetPaginationToggle((prev) => !prev);
    };

    const resetFilters = () => {
        setReplyStatus("");
        setCurrentStatus("");
        setResetPaginationToggle((prev) => !prev);
    };
    /* ===== FILTERS - END ===== */

    /* ===== STATUS BADGE COLOURS - START ===== */
    // Status cells are shown as coloured badges, the same way the Teacher /
    // Organization listings show their statuses.
    const getCurrentStatusClass = (status) => {
        switch (status) {
            case "New to StudentScholars":
                return "bg-info border-0 text-dark";
            case "Currently Registered on StudentScholars":
                return "bg-success border-0 text-light";
            case "Currently Teaching in StudentScholars":
                return "bg-primary border-0 text-light";
            default:
                return "bg-secondary border-0 text-light";
        }
    };

    const getReplyStatusClass = (reply_status) => {
        return reply_status === 1 ? "bg-success border-0 text-light" : "bg-warning border-0 text-dark";
    };
    /* ===== STATUS BADGE COLOURS - END ===== */

    const handleGetData = () => {
        isLoader(true)
        // OLD CODE (kept as it is) :
        // get_contactus_list({ search: search }).then((response) => {
        get_contactus_list({ search: search, reply_status: replyStatus, current_status: currentStatus }).then((response) => {
            if (response?.code == 1 && Array.isArray(response?.data)) {
                const dataWithRowCount = response.data.map((row, index) => ({ ...row, count: index + 1 }));
                setData(dataWithRowCount);
            } else {
                setData([])
            }
            isLoader(false)
        }).catch((error) => {
            // Without this the request failing (or timing out) left the loader
            // spinning and the screen on "No data Found", which reads as "there
            // are no messages" instead of "the listing could not be loaded".
            console.error('contact us listing failed :', error)
            setData([])
            isLoader(false)
        })
    }

    const handleContactUsDetails = (PersonId) => {
        navigate('/contactus-details', { state: { PersonId: PersonId } })
    }

    // The row opens the class screen that belongs to the person's role. The role
    // comes from the listing API as user_category (tbl_user.category joined on
    // tbl_contact_us.user_id) — every screen below expects a different state key:
    //   learner side (kids / above 18 / parents / after school) -> Subscription Class
    //   teacher                                                 -> Teaching Class
    //   organization                                            -> Org Class List
    //   organization teacher                                    -> Org Teacher Teaching Class
    // The teacher screens also carry the login details for their Admin Login flow,
    // the same way the Teacher / Org Teacher listings pass them.
    const getClassDetailsRoute = (row) => {
        // resolved_user_id is the account the listing API matched for this message
        // (by user_id, then by Id Code, then by email) — row.user_id itself is '0'
        // on every message sent while logged out. Without an account there is no
        // class screen to open.
        const userId = row?.resolved_user_id || row?.user_id
        if (!userId || userId == 0 || !row?.user_category) return null

        const teacherState = {
            teacher_id: userId,
            teacher_email: row?.user_email,
            teacher_password: row?.user_password,
            teacher_category: row?.user_category,
            teacher_login_type: row?.user_login_type,
            teacher_social_id: row?.user_social_id,
        }

        switch (row?.user_category) {
            case 'learner':
            case 'above_18':
            case 'below_18':
            case 'after_school':
                return {
                    path: '/subscription-class-details',
                    state: { user_id: userId, learner_name: row?.user_full_name || row?.name }
                }
            case 'teacher':
                return { path: '/teaching-class-details', state: teacherState }
            case 'organization':
                return { path: '/org_class_list', state: { org_id: userId } }
            case 'org_teacher':
                return { path: '/org_teacher_teaching_class_details', state: teacherState }
            default:
                return null
        }
    }

    // Guest messages (no account behind them) and any unknown role have no class
    // screen to open, so they fall back to the ContactUs details as before.
    const handleRowClick = (row) => {
        const target = getClassDetailsRoute(row)
        if (target) {
            navigate(target.path, { state: target.state })
        } else {
            handleContactUsDetails(row?.id)
        }
    }

    const columns = [
        {
            name: 'No',
            selector: row => row.count,
            sortable: false,
            width: "100px"
        },
        {
            name: 'Unique Code',
            selector: row => row.unique_code,
            sortable: true,
            width: "150px",
            cell: row => (
                <span className={`${row.reply_status === 0 ? 'fw-bold' : ''} ${row.current_status === "New to StudentScholars" ? 'text-purple' : ''}`}
                    onClick={() => handleContactUsDetails(row.id)}
                >
                    {row.unique_code == null ? "0000" : row.unique_code}
                </span>
            ),
        },
        {
            name: 'Id Code',
            selector: row => row.code,
            sortable: true,
            width: "150px",
            cell: row => (
                <span className={`${row.reply_status === 0 ? 'fw-bold' : ''} ${row.current_status === "New to StudentScholars" ? 'text-purple' : ''}`}
                    onClick={() => handleContactUsDetails(row.id)}
                >
                    {row.code == null ? "0000" : row.code}
                </span>
            ),
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            width: "200px",
            // Only the Name opens the role's class screen — every other cell keeps
            // opening the ContactUs details as before.
            cell: row => (
                <span
                    className={`${row.reply_status === 0 ? 'fw-bold' : ''} ${row.current_status === "New to StudentScholars" ? 'text-purple' : ''}`}
                    onClick={() => handleRowClick(row)}
                >
                    {row.name}
                </span>
            )
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
            width: "270px",
            cell: row => (
                <span className={`${row.reply_status === 0 ? 'fw-bold' : ''} ${row.current_status === "New to StudentScholars" ? 'text-purple' : ''}`}
                    onClick={() => handleContactUsDetails(row.id)}
                >
                    {row.email}
                </span>
            )
        },
        {
            name: 'Phone',
            width: "220px",
            selector: row => row.country_code + row.phone,
            sortable: true,
            cell: row => (
                <span className={`${row.reply_status === 0 ? 'fw-bold' : ''} ${row.current_status === "New to StudentScholars" ? 'text-purple' : ''}`}
                    onClick={() => handleContactUsDetails(row.id)}
                >
                    {row.country_code + row.phone ? row.country_code + row.phone : '-'}
                </span>
            )
        },
        {
            name: 'Current Status',
            width: "270px",
            selector: row => row.current_status,
            sortable: true,
            // OLD CODE (kept as it is) :
            // cell: row => (
            //     <span className={`${row.reply_status === 0 ? 'fw-bold' : ''} ${row.current_status === "New to StudentScholars" ? 'text-purple' : ''}`}
            //         onClick={() => handleContactUsDetails(row.id)}
            //     >
            //         {row.current_status}
            //     </span>
            // )
            cell: row => (
                <button className={`${getCurrentStatusClass(row.current_status)} ${row.reply_status === 0 ? 'fw-bold' : ''}`}
                    style={{ width: "220px" }}
                    onClick={() => handleContactUsDetails(row.id)}
                >
                    {row.current_status}
                </button>
            )
        },
        {
            name: 'Status of Message',
            width: "180px",
            // OLD CODE (kept as it is) :
            // cell: row => (
            //     <span className={`${row.reply_status === 0 ? 'fw-bold' : ''} ${row.current_status === "New to StudentScholars" ? 'text-purple' : ''}`}
            //         onClick={() => handleContactUsDetails(row.id)}
            //     >
            //         {row.reply_status === 1 ? 'Replied' : 'New'}
            //     </span>
            // ),
            // selector added so the column sorts by the shown value
            selector: row => (row.reply_status === 1 ? 'Replied' : 'New'),
            cell: row => (
                <button className={`${getReplyStatusClass(row.reply_status)} ${row.reply_status === 0 ? 'fw-bold' : ''}`}
                    style={{ width: "80px" }}
                    onClick={() => handleContactUsDetails(row.id)}
                >
                    {row.reply_status === 1 ? 'Replied' : 'New'}
                </button>
            ),
            sortable: true,
        },
        {
            name: 'Actions',
            width: "100px",
            cell: (row) => (
                <div className="d-flex justify-content-center">
                    <div className="col me-2">
                        <FontAwesome
                            name="view"
                            className="fs-5 fa-solid fa-eye  mx-2 my-2 bg-light text-secondary"
                            onClick={() => handleContactUsDetails(row.id)}
                        ></FontAwesome>
                    </div>
                </div>
            ),
        }
    ]

    useEffect(() => {
        handleGetData()
        // OLD CODE (kept as it is) :
        // }, [debounceSearch])
    }, [debounceSearch, replyStatus, currentStatus])

    return <>
        <JqueryLoad />
        <Hdashboard />
        <main>
            <div className="dashboard-app d-lg-flex">
                <header className="dashboard-toolbar d-lg-flex justify-content-between">
                    <div className="align-items-center justify-content-between d-flex">
                        <div className="d-flex align-items-center">
                            <Link className="menu-toggle align-items-center d-lg-none d-flex">
                                <i className="fa fa-bars" />
                            </Link>
                            <Link to="index.php" className="d-lg-none d-block brand-logo">
                                <img src="./assets/images/logo.png" alt="logo" />
                            </Link>
                        </div>
                    </div>
                </header>
                <>
                    <div className="fs-3 fw-500 mt-4 ms-4">
                        ContactUs List
                    </div>
                    <div className="d-flex justify-content-end align-items-center mb-3 search-container">
                        {/* ===== FILTERS - START ===== */}
                        <div className="d-flex align-items-center me-3">
                            <div className="dropdown">
                                <button className="btn border-purple rounded-cricle rounded-pill dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    Status of Message
                                </button>
                                <div className="dropdown-menu class_size_start filter_box">
                                    <h3 className="fs-5 mb-3">Status of Message</h3>
                                    <form className="" action="" id="">
                                        {[
                                            { value: "", label: "All" },
                                            { value: "new", label: "New" },
                                            { value: "replied", label: "Replied" },
                                        ].map((option) => (
                                            <div className="form-check" key={option.value || "all"}>
                                                <input className="form-check-input" type="radio" name="contactReplyStatusFilter" id={`contactReplyStatus_${option.value || "all"}`}
                                                    onChange={() => handleReplyStatusFilter(option.value)} checked={replyStatus == option.value} />
                                                <label className="form-check-label" htmlFor={`contactReplyStatus_${option.value || "all"}`}>
                                                    {option.label}
                                                </label>
                                            </div>
                                        ))}
                                    </form>
                                </div>
                            </div>
                            {/* Current Status filter hidden (kept for later use)
                            <div className="dropdown ms-2">
                                <button className="btn border-purple rounded-cricle rounded-pill dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    Current Status
                                </button>
                                <div className="dropdown-menu class_size_start filter_box">
                                    <h3 className="fs-5 mb-3">Current Status</h3>
                                    <form className="" action="" id="">
                                        {["", ...currentStatusOptions].map((option, index) => (
                                            <div className="form-check" key={option || "all"}>
                                                <input className="form-check-input" type="radio" name="contactCurrentStatusFilter" id={`contactCurrentStatus_${index}`}
                                                    onChange={() => handleCurrentStatusFilter(option)} checked={currentStatus == option} />
                                                <label className="form-check-label" htmlFor={`contactCurrentStatus_${index}`}>
                                                    {option || "All"}
                                                </label>
                                            </div>
                                        ))}
                                    </form>
                                </div>
                            </div>
                            */}
                            <div className="ms-4" onClick={resetFilters}><a href="#" onClick={(e) => e.preventDefault()} className="text-purple fw-500">Reset Filters</a></div>
                        </div>
                        {/* ===== FILTERS - END ===== */}
                        <div className="row me-5">
                            <div className="col">
                                <CInputGroup>
                                    <CFormInput type="text" placeholder="Search" onChange={(e) => setSearch(e.target.value)} />
                                    <CButton style={{ backgroundColor: "rgb(87, 8, 97)", color: "light" }}>
                                        <FontAwesome className="fa-solid fa-search" name="search" />
                                    </CButton>
                                </CInputGroup>
                            </div>
                        </div>
                    </div>
                    <div className="px-5 mt-1">
                        <div>
                            {(
                                data && data.length > 0 ? (
                                    <>
                                        {/* OLD CODE (kept as it is) : <DataTable title="" columns={columns} data={data} pagination customStyles={customStyles} /> */}
                                        <DataTable title="" columns={columns} data={data} pagination paginationResetDefaultPage={resetPaginationToggle} customStyles={customStyles} />
                                    </>
                                ) : (
                                    <p className="mt-5 text-center text-purple fw-500 fs-3 mt-5">No data Found</p>
                                )
                            )}
                        </div>
                    </div>
                </>
            </div>
        </main>
    </>
}